import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "July 3, 2026";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold font-headline text-foreground mb-3">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — PostWriter</title>
        <meta
          name="description"
          content="How PostWriter collects, uses, and protects your data."
        />
        <meta property="og:url" content="https://postwriter.tech/privacy" />
        <meta property="og:title" content="Privacy Policy — PostWriter" />
      </Helmet>
      <main className="min-h-screen bg-background">
        <header className="border-b border-border">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
              <span className="text-lg font-bold font-headline text-foreground">PostWriter</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 py-12">
          <h1 className="text-3xl font-bold font-headline text-foreground mb-2">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-10 font-mono">
            Last updated: {LAST_UPDATED}
          </p>

          <Section title="1. Who we are">
            <p>
              PostWriter (&ldquo;PostWriter&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) is an
              AI-powered LinkedIn content tool available at{" "}
              <a href="https://postwriter.tech" className="text-primary hover:underline">
                postwriter.tech
              </a>
              , operated by Amrit Singh. For any privacy questions, contact us at{" "}
              <a href="mailto:amrit@postwriter.tech" className="text-primary hover:underline">
                amrit@postwriter.tech
              </a>
              .
            </p>
          </Section>

          <Section title="2. Information we collect">
            <p>
              <strong className="text-foreground">Account information.</strong> When you sign in
              with Google, we receive your name, email address, and profile picture from Google. We
              do not receive or store your Google password.
            </p>
            <p>
              <strong className="text-foreground">Profile and preferences.</strong> Information you
              provide in the app: your role, selected topics, tech stack, preferred post styles,
              and career experiences you add to personalize generated posts.
            </p>
            <p>
              <strong className="text-foreground">Content.</strong> Posts generated for you,
              drafts you edit, posts you save, star, or mark as used, and carousels you create.
            </p>
            <p>
              <strong className="text-foreground">Payment information.</strong> Payments are
              processed by Lemon Squeezy, our merchant of record. We never see or store your card
              details. We receive subscription status, plan, and billing country from Lemon
              Squeezy to manage your account.
            </p>
            <p>
              <strong className="text-foreground">Usage data.</strong> We use analytics (PostHog)
              to understand how the product is used — pages visited, features used, and device or
              browser information. We use this to improve the product.
            </p>
          </Section>

          <Section title="3. How we use your information">
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide the service: generate personalized posts, research topics, and maintain your library</li>
              <li>Process subscriptions and manage your plan</li>
              <li>Send service emails (onboarding, billing, important account notices)</li>
              <li>Improve the product through aggregated usage analytics</li>
              <li>Prevent abuse and enforce our Terms of Service</li>
            </ul>
            <p>We do not sell your personal information to anyone.</p>
          </Section>

          <Section title="4. AI processing">
            <p>
              To generate posts, we send your topics, preferences, and the experiences you provide
              to third-party AI providers (currently OpenAI and Google). These providers process
              the data to return generated content and, per their API terms, do not use API data
              to train their models. Do not include sensitive personal information (such as health
              or financial details) in the experiences you add.
            </p>
          </Section>

          <Section title="5. Third-party services">
            <p>We share data only with the service providers needed to run PostWriter:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Google — sign-in (OAuth)</li>
              <li>Lemon Squeezy — payments and billing (merchant of record)</li>
              <li>OpenAI and Google — AI content generation</li>
              <li>PostHog — product analytics</li>
              <li>Amazon Web Services — hosting and infrastructure</li>
            </ul>
            <p>
              Each provider processes data under its own privacy policy and only to provide its
              service to us.
            </p>
          </Section>

          <Section title="6. Data retention and deletion">
            <p>
              We keep your data while your account is active. You can request deletion of your
              account and all associated data at any time by emailing{" "}
              <a href="mailto:amrit@postwriter.tech" className="text-primary hover:underline">
                amrit@postwriter.tech
              </a>
              . We will delete your data within 30 days, except records we must keep for legal or
              accounting purposes (such as invoices).
            </p>
          </Section>

          <Section title="7. Your rights">
            <p>
              Depending on where you live (including under India&rsquo;s Digital Personal Data
              Protection Act, the EU GDPR, or similar laws), you may have the right to access,
              correct, export, or delete your personal data, and to withdraw consent. To exercise
              any of these rights, email us and we will respond within 30 days.
            </p>
          </Section>

          <Section title="8. Security">
            <p>
              We use industry-standard measures to protect your data, including encryption in
              transit (HTTPS), access controls, and secure cloud infrastructure. No system is 100%
              secure, but we work to protect your information and will notify you of any breach as
              required by law.
            </p>
          </Section>

          <Section title="9. Cookies">
            <p>
              We use cookies and similar technologies for authentication (keeping you signed in)
              and analytics. You can control cookies through your browser settings; disabling
              authentication cookies will prevent you from staying signed in.
            </p>
          </Section>

          <Section title="10. Children">
            <p>
              PostWriter is not intended for anyone under 18, and we do not knowingly collect data
              from children.
            </p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>
              We may update this policy as the product evolves. We will post changes here and
              update the date above. For material changes, we will notify you by email or in the
              app.
            </p>
          </Section>
        </div>
      </main>
    </>
  );
}
